import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X, Loader2 } from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: "Hi! I'm the HumanScaled assistant. I can answer questions about our platform, pricing, how operators work, or how businesses can save with AI operations. What would you like to know?",
};

const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { scrollRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/hub/ai/generate`, {
        prompt: userMsg,
        system_message: `You are the HumanScaled website assistant. HumanScaled provides Controlled AI Infrastructure — human control, machine execution, measured results. Answer questions about:
- For Businesses: how to find certified operators, payroll savings, SLA guarantees, integration with CRMs
- For Operators: how to build workflows, pricing tiers ($297/$497/$997), income potential, tier progression
- Platform features: monitoring, guardrails, blueprints, marketplace
Be concise (under 100 words). Professional tone. No hype.`,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I had trouble processing that. Try asking about our pricing, how operators work, or what businesses can expect.' }]);
    } finally { setLoading(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20 hover:bg-emerald-400 transition-colors" data-testid="chat-bubble">
        <MessageCircle className="w-6 h-6 text-black" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 bg-[#0a0a0a] border border-white/10 shadow-2xl flex flex-col" style={{ height: 440 }} data-testid="live-chat">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <HSLogo size={20} />
          <span className="text-sm font-bold">HumanScaled</span>
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
        </div>
        <button onClick={() => setOpen(false)} className="p-1 text-muted-foreground hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-3 py-2 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-white/5 border border-white/10'}`}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-3 py-2 bg-white/5 border border-white/10"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-white/5 flex gap-2">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSend()} placeholder="Ask anything..." className="flex-1 bg-black/30 border border-white/10 rounded-sm px-3 py-2 text-sm text-white placeholder:text-muted-foreground focus:outline-none focus:border-emerald-500/50" data-testid="chat-input" />
        <button onClick={handleSend} disabled={loading || !input.trim()} className="bg-emerald-500 text-black px-3 rounded-sm hover:bg-emerald-400 disabled:opacity-50" data-testid="chat-send"><Send className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export default LiveChat;
